/** Homepage copy sourced from https://isbm.ict4sd.org/index.php */
import { BANGKOK } from './siteImagery.js';

/**
 * Curated delegate & speaker portraits for the homepage hero mosaic.
 * Order matches bento grid slots:
 * Index 0 (Slot 0): 2x2 featured space (Asian Woman, glasses, red shirt)
 * Index 1 (Slot 1): 1x1 space (Man, podium speaker)
 * Index 2 (Slot 2): 1x1 space (Man, striped tie)
 * Index 3 (Slot 3): 1x1 space (Speaker, 2025/home/11.webp)
 * Index 4 (Slot 4): 1x2 tall space (Curly Hair Woman, glasses)
 * Index 5 (Slot 5): 1x1 space (Researcher presenter, 2024/home/8.webp)
 * Index 6 (Slot 6): 1x1 space (Delegate, 2024/home/11.webp)
 * Index 7 (Slot 7): 1x1 space (Polka-dot Dress Woman, glasses)
 */
export const HERO_PEOPLE_PHOTOS = [
  {
    src: '/assets/images/gallery/2023/2.webp',
    alt: 'ISBM participant speaking at a session — Bangkok',
    objectPosition: 'center 12%',
    featured: true,
  },
  {
    src: '/assets/images/gallery/2024/home/4.webp',
    alt: 'ISBM presenter at the podium — Bangkok 2024',
    objectPosition: 'center 6%',
  },
  {
    src: '/assets/images/gallery/2024/home/9.webp',
    alt: 'ISBM delegate presenting at the conference',
    objectPosition: 'center 8%',
  },
  {
    src: '/assets/images/gallery/2025/home/11.webp',
    alt: 'ISBM speaker addressing the audience',
    objectPosition: 'center 10%',
  },
  {
    src: '/assets/images/gallery/2023/4.webp',
    alt: 'ISBM keynote speaker with microphone',
    objectPosition: 'center 5%',
    featured: true,
  },
  {
    src: '/assets/images/gallery/2024/home/8.webp',
    alt: 'ISBM researcher presenting findings',
    objectPosition: 'center 8%',
  },
  {
    src: '/assets/images/gallery/2024/home/11.webp',
    alt: 'ISBM delegate giving a presentation',
    objectPosition: 'center 8%',
  },
  {
    src: '/assets/images/gallery/2025/home/9.webp',
    alt: 'ISBM conference speaker in session',
    objectPosition: 'center 10%',
    featured: true,
  },
];

// `dwell` controls how long each slide is held before auto-advancing (ms).
// The opening people-mosaic and skyline slides linger longer (4s); the closing
// scenic slides move along quicker (2.6s).
export const HERO_SLIDES = [
  {
    id: 'people-mosaic',
    edition: '5th',
    title: 'World Conference on',
    subtitle: 'Information Systems for Business Management',
    tagline: 'Where Global Researchers, Leaders & Innovators Connect',
    peopleMosaic: true,
    image: HERO_PEOPLE_PHOTOS[0].src,
    dwell: 4000,
  },
  {
    id: 'bangkok-night',
    edition: '5th',
    title: 'World Conference on',
    subtitle: 'Information Systems for Business Management',
    tagline: 'This Doesn\'t Happen Overnight but Leads to a Brand',
    image: '/assets/images/home-page.webp',
    dwell: 2600,
  },
  {
    id: 'bangkok-night-alt',
    edition: '5th',
    title: 'World Conference on',
    subtitle: 'Information Systems for Business Management',
    tagline: 'This Doesn\'t Happen Overnight but Leads to a Brand',
    image: '/assets/images/bangkok.webp',
    dwell: 2600,
  },
];

export const HYBRID_NOTE =
  'This Fifth Edition of the conference will be in Hybrid Mode — 17 September 2026 (Physical & Digital Mode) · 18–19 September 2026 (Digital Mode)';

export const CONFERENCE_TITLE = {
  editionNumber: 5,
  editionOrdinal: 'th',
  overline: 'World Conference on',
  headline: ['Information', 'Systems'],
  subline: 'for Business Management',
};

export const INTRODUCTION = {
  eyebrow: 'Introduction & Background',
  title: CONFERENCE_TITLE,
  heading: 'World Conference on Information Systems for Business Management',
  paragraphs: [
    'The 5th World Conference on Information Systems for Business Management (ISBM) is one of the most respected global gatherings dedicated to data, governance, and modern enterprise. Now entering its fifth edition, ISBM convenes investors, top executives, pioneering researchers, and policy leaders who are collectively shaping how organizations integrate Social, Corporate, Data, IPR, and Environmental priorities into the strategic decisions that matter most.',
    'In an era defined by information, every institution — from boardrooms to research institutions — is being rebuilt around intelligent systems and responsible governance. ISBM sits at that crossroads, creating the intellectual space where technology, strategy, and policy converge to drive real-world impact across business, academia, and society.',
    'Each edition draws delegates from more than 50 nations who present original research with direct implications for global practice. Proceedings are published by Springer Nature and indexed in Scopus, Web of Science, DBLP, and Google Scholar — ensuring the conference\'s contributions endure as part of the permanent global academic record.',
    'ISBM 2026 in Bangkok is more than a conference. It is a world-class platform for the ideas, dialogue, and breakthroughs that will define the next era of Information Management.',
  ],
};

export const OBJECTIVES = {
  title: 'Objectives — Shaping the Future with ISBM',
  items: [
    'The conference is anticipated to attract a large number of high quality submissions and stimulate the cutting-edge research discussions among many strategists, Managers, academic pioneering researchers, scientists, industrial engineers, students, directors, scientists, lawyers, Policy experts, Information technology professionals from all around the world and provide a forum to discuss more on the theme.',
    'Propose new technologies, share their experiences and discuss future solutions for design infrastructure. Discuss new case studies which haven\'t come across, share their experiences and discuss future impacts and possible solutions for Good Technology Governance. Support and Share greater and more impactful RECOMMENDATION for Global GOOD Governance Policies.',
    'Provide common platform for academic pioneering researchers, scientists, engineers, managers and students to share their views and achievements. Enrich technocrats, management professionals and academicians by presenting their innovative and constructive ideas. Focus on innovative issues at international level by bringing together the experts from different countries.',
  ],
};

export const PARTICIPANTS = {
  title: 'ISBM 2026 - A Global Platform For All The Researchers & Innovators',
  image: '/gallery/2024/home/6.webp',
  roles: [
    'Well-known scholars',
    'Investors',
    'ESG experts',
    'Thought leaders',
    'Strategists',
    'Decision Makers',
    'Policy Makers',
    'CEO',
    'CTO',
    'Governance Managers',
    'Business Analytics',
    'Business Analysts',
    'Management Reviewee',
    'Data scientists',
    'Information Technology Professionals',
    'Technocrats',
    'Enterprise information leaders',
    'Data management infrastructure managers',
    'Professors',
    'Lecturers',
    'PhD candidates and early career scholars',
  ],
};

export const PARTNERS = [
  { label: 'Local Organizing Partner', logo: '/assets/images/logo/new-iadd-logo.webp', alt: 'IADD — International Auditors for Digital and Data Management Association' },
  {
    label: 'Organized by',
    logo: '/assets/images/Conferences Logo.png',
    alt: 'GR Conferences',
    logoClassName: 'w-full h-auto max-h-none object-contain scale-[1.18] dark:invert',
    logoAreaClassName: 'min-h-[120px] sm:min-h-[132px] lg:min-h-[144px]',
    cardClassName: 'ring-1 ring-brand-200/40 dark:ring-brand-500/20',
    wide: true,
  },
  { label: 'Publication Partner', logo: '/assets/images/logo/new-springer-logo.webp', alt: 'Springer Nature', logoClassName: 'w-full object-contain scale-[1.4]' },
];

export const PROCEEDING_YEARS = [2025, 2024, 2023, 2022];

export const HOME_STATS = [
  { value: 5, suffix: '', ordinal: 'th', label: 'Conference edition' },
  { value: 50, suffix: '+', label: 'Nations representation' },
  { value: 550, suffix: '+', label: 'Global Presentations' },
  { value: 1500, suffix: '+', label: 'Attendees' },
];
